import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Plus, Loader2, Users, Coins, Download, History,
  Pencil, Trash2, Banknote, Settings, ArrowUpCircle, ArrowDownCircle, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const fmtMoney = (v, c = 'FCFA') => `${Math.round(Number(v || 0)).toLocaleString('fr-FR').replace(/,/g, ' ')} ${c}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso.slice(0, 10); }
};

const OP_LABELS = {
  acquisition: { label: 'Acquisition', color: 'bg-emerald-100 text-emerald-700', icon: ArrowUpCircle },
  cession: { label: 'Cession', color: 'bg-red-100 text-red-700', icon: ArrowDownCircle },
  augmentation: { label: 'Augmentation', color: 'bg-blue-100 text-blue-700', icon: ArrowUpCircle },
  reduction: { label: 'Reduction', color: 'bg-orange-100 text-orange-700', icon: ArrowDownCircle },
};

const Shareholders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  const [capital, setCapital] = useState(null);
  const [shareholders, setShareholders] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCapital, setShowCapital] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showOps, setShowOps] = useState(null); // shareholder id
  const [opsList, setOpsList] = useState([]);
  const [showOpForm, setShowOpForm] = useState(false);
  const [showDividend, setShowDividend] = useState(false);

  const [capForm, setCapForm] = useState({ total_shares: 0, share_nominal_value: 0, currency: 'FCFA', company_name: '', rccm: '' });
  const [createForm, setCreateForm] = useState({ user_id: '', shares_count: 0, entry_date: '', notes: '' });
  const [opForm, setOpForm] = useState({ type: 'acquisition', shares_delta: 0, date: '', notes: '' });
  const [divForm, setDivForm] = useState({ total_amount: 0, distribution_date: '', period_label: '', notes: '' });

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Reserve a l'administrateur");
      navigate('/');
    }
  }, [isAdmin, navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cap, sh, div, us] = await Promise.all([
        api.get('/shareholders/capital'),
        api.get('/shareholders'),
        api.get('/dividends'),
        api.get('/users'),
      ]);
      setCapital(cap.data);
      setShareholders(sh.data || []);
      setDividends(div.data || []);
      setUsers(us.data || []);
      setCapForm({
        total_shares: cap.data.total_shares || 0,
        share_nominal_value: cap.data.share_nominal_value || 0,
        currency: cap.data.currency || 'FCFA',
        company_name: cap.data.company_name || '',
        rccm: cap.data.rccm || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  const availableUsers = useMemo(() => {
    const taken = new Set(shareholders.map(s => s.user_id));
    return users.filter(u => !taken.has(u.id));
  }, [users, shareholders]);

  // Capital
  const handleSaveCapital = async () => {
    try {
      const r = await api.put('/shareholders/capital', {
        ...capForm,
        total_shares: Number(capForm.total_shares),
        share_nominal_value: Number(capForm.share_nominal_value),
      });
      setCapital(r.data);
      toast.success('Capital social mis a jour');
      setShowCapital(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // Create shareholder
  const handleCreate = async () => {
    if (!createForm.user_id) return toast.error('Selectionnez un collaborateur');
    if (Number(createForm.shares_count) <= 0) return toast.error('Nombre de parts > 0');
    try {
      await api.post('/shareholders', {
        ...createForm,
        shares_count: Number(createForm.shares_count),
      });
      toast.success('Actionnaire ajoute');
      setShowCreate(false);
      setCreateForm({ user_id: '', shares_count: 0, entry_date: '', notes: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Retirer ${s.user?.name} du capital ?`)) return;
    try {
      await api.delete(`/shareholders/${s.id}`);
      toast.success('Actionnaire retire');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadCert = async (s) => {
    try {
      toast.info('Generation du certificat...');
      const r = await api.get(`/shareholders/${s.id}/certificate/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `certificat_actionnariat_${(s.user?.name || 'actionnaire').replace(/\s+/g, '_').toLowerCase()}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const openOps = async (s) => {
    setShowOps(s);
    try {
      const r = await api.get(`/shareholders/${s.id}/operations`);
      setOpsList(r.data || []);
    } catch { setOpsList([]); }
  };

  const handleSubmitOp = async () => {
    if (Number(opForm.shares_delta) <= 0) return toast.error('Nombre de parts > 0');
    try {
      await api.post(`/shareholders/${showOps.id}/operations`, {
        ...opForm,
        shares_delta: Number(opForm.shares_delta),
      });
      toast.success('Operation enregistree');
      setShowOpForm(false);
      setOpForm({ type: 'acquisition', shares_delta: 0, date: '', notes: '' });
      // refresh
      const r = await api.get(`/shareholders/${showOps.id}/operations`);
      setOpsList(r.data || []);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateDividend = async () => {
    if (Number(divForm.total_amount) <= 0) return toast.error('Montant > 0');
    try {
      await api.post('/dividends', {
        ...divForm,
        total_amount: Number(divForm.total_amount),
      });
      toast.success('Distribution creee');
      setShowDividend(false);
      setDivForm({ total_amount: 0, distribution_date: '', period_label: '', notes: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handlePayLine = async (did, user_id) => {
    try {
      await api.put(`/dividends/${did}/lines/${user_id}/pay`);
      toast.success('Marque comme paye');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (!isAdmin) return null;

  const currency = capital?.currency || 'FCFA';
  const capitalTotalValue = (capital?.total_shares || 0) * (capital?.share_nominal_value || 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shareholders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#D4A745]" />
            Actionnariat des collaborateurs
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gerer le capital social, les parts et les dividendes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCapital(true)} data-testid="open-capital-btn">
            <Settings className="w-4 h-4 mr-2" /> Capital social
          </Button>
          <Button onClick={() => setShowCreate(true)} disabled={!capital?.total_shares} data-testid="open-create-btn"
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]">
            <Plus className="w-4 h-4 mr-2" /> Nouvel actionnaire
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Coins className="w-5 h-5" />} label="Capital social" value={fmtMoney(capitalTotalValue, currency)} color="bg-[#1B3A4B]" />
        <KpiCard icon={<TrendingUp className="w-5 h-5" />} label="Parts totales" value={`${(capital?.total_shares || 0).toLocaleString('fr-FR')}`} color="bg-emerald-600" />
        <KpiCard icon={<Users className="w-5 h-5" />} label="Parts attribuees" value={`${(capital?.allocated_shares || 0).toLocaleString('fr-FR')} / ${(capital?.total_shares || 0).toLocaleString('fr-FR')}`} color="bg-blue-600" />
        <KpiCard icon={<Banknote className="w-5 h-5" />} label="Parts restantes" value={`${(capital?.remaining_shares || 0).toLocaleString('fr-FR')}`} color="bg-[#D4A745]" />
      </div>

      <Tabs defaultValue="shareholders">
        <TabsList>
          <TabsTrigger value="shareholders" data-testid="tab-shareholders">Actionnaires ({shareholders.length})</TabsTrigger>
          <TabsTrigger value="dividends" data-testid="tab-dividends">Dividendes ({dividends.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="shareholders" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-7 h-7 animate-spin text-[#1B3A4B] mx-auto" /></div>
              ) : shareholders.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun actionnaire pour le moment</p>
                  {!capital?.total_shares && <p className="text-xs mt-2">Commencez par configurer le capital social</p>}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Collaborateur</th>
                      <th className="px-4 py-3">Parts</th>
                      <th className="px-4 py-3">%</th>
                      <th className="px-4 py-3">Valeur</th>
                      <th className="px-4 py-3">Date d'entree</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareholders.map(s => (
                      <tr key={s.id} className="border-t hover:bg-gray-50" data-testid={`shareholder-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#1B3A4B]">{s.user?.name || '—'}</div>
                          <div className="text-xs text-gray-400">{s.user?.role}</div>
                        </td>
                        <td className="px-4 py-3 font-mono font-semibold">{(s.shares_count || 0).toLocaleString('fr-FR')}</td>
                        <td className="px-4 py-3"><Badge className="bg-[#D4A745]/20 text-[#1B3A4B]">{s.ownership_percent}%</Badge></td>
                        <td className="px-4 py-3 font-mono">{fmtMoney(s.shares_value, currency)}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(s.entry_date)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openOps(s)} title="Historique" data-testid={`history-${s.id}`}>
                              <History className="w-4 h-4 text-gray-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDownloadCert(s)} title="Certificat PDF" data-testid={`cert-${s.id}`}>
                              <Download className="w-4 h-4 text-[#D4A745]" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} title="Retirer" data-testid={`delete-${s.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dividends" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setShowDividend(true)} disabled={shareholders.length === 0} data-testid="open-dividend-btn"
                    className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Distribuer un dividende
            </Button>
          </div>
          {dividends.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-gray-400">
              <Banknote className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune distribution effectuee</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {dividends.map(d => (
                <Card key={d.id} data-testid={`dividend-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <p className="font-semibold text-[#1B3A4B]">
                          {d.period_label || 'Distribution'} <span className="text-gray-400 text-sm">— {fmtDate(d.distribution_date)}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Total: <b>{fmtMoney(d.total_amount, currency)}</b> /
                          Per part: <b>{fmtMoney(d.per_share_amount, currency)}</b> /
                          {d.shares_at_distribution} parts en circulation
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {d.lines?.map(line => (
                          <div key={line.user_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{line.user_name}</p>
                              <p className="text-xs text-gray-500">{line.shares} parts → <b>{fmtMoney(line.amount, currency)}</b></p>
                            </div>
                            {line.paid ? (
                              <Badge className="bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-1" /> Paye</Badge>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handlePayLine(d.id, line.user_id)} data-testid={`pay-${d.id}-${line.user_id}`}>
                                Marquer paye
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* === Capital modal === */}
      <Dialog open={showCapital} onOpenChange={setShowCapital}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configurer le capital social</DialogTitle>
            <DialogDescription>Definit le nombre total de parts et leur valeur unitaire.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Raison sociale</Label>
              <Input value={capForm.company_name} onChange={e => setCapForm(f => ({ ...f, company_name: e.target.value }))} data-testid="capital-company" />
            </div>
            <div>
              <Label>RCCM</Label>
              <Input value={capForm.rccm} onChange={e => setCapForm(f => ({ ...f, rccm: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre total de parts</Label>
                <Input type="number" min="0" value={capForm.total_shares} onChange={e => setCapForm(f => ({ ...f, total_shares: e.target.value }))} data-testid="capital-total" />
              </div>
              <div>
                <Label>Valeur unitaire (FCFA)</Label>
                <Input type="number" min="0" value={capForm.share_nominal_value} onChange={e => setCapForm(f => ({ ...f, share_nominal_value: e.target.value }))} data-testid="capital-nominal" />
              </div>
            </div>
            <p className="text-xs text-gray-500">Capital total = <b>{fmtMoney((capForm.total_shares || 0) * (capForm.share_nominal_value || 0), capForm.currency)}</b></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapital(false)}>Annuler</Button>
            <Button onClick={handleSaveCapital} data-testid="capital-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Create shareholder modal === */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvel actionnaire</DialogTitle>
            <DialogDescription>Parts restantes : <b>{capital?.remaining_shares || 0}</b></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Collaborateur</Label>
              <Select value={createForm.user_id} onValueChange={v => setCreateForm(f => ({ ...f, user_id: v }))}>
                <SelectTrigger data-testid="create-user-select"><SelectValue placeholder="Selectionner un collaborateur" /></SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0
                    ? <div className="px-2 py-3 text-sm text-gray-400">Tous les collaborateurs sont deja actionnaires</div>
                    : availableUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre de parts</Label>
              <Input type="number" min="1" max={capital?.remaining_shares} value={createForm.shares_count} onChange={e => setCreateForm(f => ({ ...f, shares_count: e.target.value }))} data-testid="create-shares" />
            </div>
            <div>
              <Label>Date d'entree</Label>
              <Input type="date" value={createForm.entry_date} onChange={e => setCreateForm(f => ({ ...f, entry_date: e.target.value }))} />
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea rows={2} value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} data-testid="create-save">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Operations history modal === */}
      <Dialog open={!!showOps} onOpenChange={(o) => !o && setShowOps(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historique — {showOps?.user?.name}</DialogTitle>
            <DialogDescription>
              Parts actuelles : <b>{showOps?.shares_count} ({showOps?.ownership_percent}%)</b>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setShowOpForm(true)} className="bg-[#1B3A4B]">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle operation
            </Button>
          </div>
          {opsList.length === 0 ? (
            <p className="text-center text-gray-400 py-6">Aucune operation</p>
          ) : (
            <div className="space-y-2">
              {opsList.map(op => {
                const cfg = OP_LABELS[op.type] || OP_LABELS.acquisition;
                const Icon = cfg.icon;
                return (
                  <div key={op.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Icon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={cfg.color}>{cfg.label}</Badge>
                        <span className="font-mono font-semibold">
                          {op.type === 'cession' || op.type === 'reduction' ? '−' : '+'}{op.shares_delta} parts
                        </span>
                      </div>
                      {op.notes && <p className="text-xs text-gray-500 mt-0.5">{op.notes}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">{fmtDate(op.date)} — par {op.created_by_name || '—'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === Op form modal === */}
      <Dialog open={showOpForm} onOpenChange={setShowOpForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nouvelle operation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={opForm.type} onValueChange={v => setOpForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="acquisition">Acquisition (+)</SelectItem>
                  <SelectItem value="augmentation">Augmentation de capital (+)</SelectItem>
                  <SelectItem value="cession">Cession (−)</SelectItem>
                  <SelectItem value="reduction">Reduction (−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre de parts</Label>
              <Input type="number" min="1" value={opForm.shares_delta} onChange={e => setOpForm(f => ({ ...f, shares_delta: e.target.value }))} />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={opForm.date} onChange={e => setOpForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={opForm.notes} onChange={e => setOpForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpForm(false)}>Annuler</Button>
            <Button onClick={handleSubmitOp}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Dividend modal === */}
      <Dialog open={showDividend} onOpenChange={setShowDividend}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Distribuer un dividende</DialogTitle>
            <DialogDescription>Le montant total sera reparti automatiquement au prorata des parts detenues.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Montant total ({currency})</Label>
              <Input type="number" min="1" value={divForm.total_amount} onChange={e => setDivForm(f => ({ ...f, total_amount: e.target.value }))} data-testid="dividend-amount" />
            </div>
            <div>
              <Label>Date de distribution</Label>
              <Input type="date" value={divForm.distribution_date} onChange={e => setDivForm(f => ({ ...f, distribution_date: e.target.value }))} />
            </div>
            <div>
              <Label>Periode / Libelle</Label>
              <Input placeholder="Ex: Exercice 2025" value={divForm.period_label} onChange={e => setDivForm(f => ({ ...f, period_label: e.target.value }))} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={divForm.notes} onChange={e => setDivForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            {divForm.total_amount > 0 && (capital?.allocated_shares || 0) > 0 && (
              <p className="text-xs text-gray-500">
                Par part : <b>{fmtMoney(divForm.total_amount / capital.allocated_shares, currency)}</b>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDividend(false)}>Annuler</Button>
            <Button onClick={handleCreateDividend} className="bg-emerald-600" data-testid="dividend-save">Distribuer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ icon, label, value, color }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} text-white flex items-center justify-center`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="font-bold text-[#1B3A4B] truncate">{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default Shareholders;
